// Situation playbooks used by Instant Mode and Situation Engine.
// Kept plain data so the interface layers stay dumb.

export const SITUATIONS = [
  {
    id: "emergency",
    label: "EMERGENCY",
    icon: "AlertTriangle",
    tone: "red",
    subtitle: "Life or safety at risk",
    right_now: [
      "Move away from immediate danger if you can.",
      "Call your local emergency number now.",
      "Tell them your exact location and what you can see.",
      "Stay on the line and follow instructions.",
    ],
    next: ["Contact a trusted person from your Trust Circle", "Keep your phone visible and charged", "Do not put yourself between the person and the danger"],
    checklist: ["Are you safe right now?", "Is anyone injured?", "Is help on the way?", "Is your phone reachable?"],
    default_message: "There is an emergency. I need help.",
  },
  {
    id: "lost",
    label: "I'M LOST",
    icon: "Compass",
    tone: "cyan",
    subtitle: "You don't know where you are",
    right_now: [
      "Stop and move to a visible, safe location.",
      "Check your phone battery and signal.",
      "Share your location with a trusted person.",
      "Avoid isolated routes.",
      "Identify the nearest safe public place.",
    ],
    next: ["Contact a trusted person", "Check the map", "Move toward a safe public location", "Keep your phone accessible"],
    checklist: ["Can you see any landmark?", "Do you have signal?", "Is there a public place nearby?", "Have you told someone?"],
    default_message: "I'm currently lost and need assistance.",
  },
  {
    id: "find",
    label: "FIND SOMEONE",
    icon: "SearchCheck",
    tone: "cyan",
    subtitle: "Someone is separated from you",
    right_now: [
      "Stay near the last place you saw them.",
      "Call their phone or their guardian.",
      "Ask nearby staff or security for help.",
      "Note what they were wearing and where they were last seen.",
    ],
    next: ["Move to the venue information desk", "Alert on-site security", "Share their description with people nearby", "Notify family"],
    checklist: ["Last location?", "What were they wearing?", "Do they carry a phone?", "Have you alerted staff?"],
    default_message: "I am trying to locate someone who is separated from me.",
  },
  {
    id: "help",
    label: "HELP SOMEONE",
    icon: "HeartHandshake",
    tone: "lime",
    subtitle: "You want to help — safely",
    right_now: [
      "Assess whether it is safe for YOU to approach.",
      "Ask if they need help before doing anything.",
      "Call appropriate emergency services.",
      "Do not put yourself in danger.",
      "Provide basic assistance you are trained for.",
    ],
    next: ["Stay with them until help arrives if safe", "Keep bystanders back and calm", "Note what happened for responders"],
    checklist: ["Is the scene safe?", "Are they responsive?", "Is help called?", "Are you the closest capable helper?"],
    default_message: "I am with someone who needs help and I am assisting.",
  },
  {
    id: "someone-needs",
    label: "SOMEONE NEEDS HELP",
    icon: "Siren",
    tone: "red",
    subtitle: "A person nearby is in trouble",
    right_now: [
      "Check what type of help is needed.",
      "Call emergency services if injury or danger.",
      "Do not move an injured person unless there is direct danger.",
      "Keep the area clear and calm.",
    ],
    next: ["Follow professional instructions", "Stay with the person if safe", "Preserve the scene for responders"],
    checklist: ["Are they conscious?", "Are they breathing?", "Is help on the way?", "Is the area safe?"],
    default_message: "Someone near me needs help. I am with them.",
  },
  {
    id: "scam",
    label: "POSSIBLE SCAM",
    icon: "ShieldAlert",
    tone: "amber",
    subtitle: "Something feels wrong online",
    right_now: [
      "STOP. Do not reply, click or share anything.",
      "Never share OTPs, PINs or passwords.",
      "Verify by calling the official number from the company's website.",
      "Screenshot the message and number.",
    ],
    next: ["Change passwords on affected accounts", "Report to your bank fraud line", "Report to your national cybercrime helpline", "Warn family who could be next"],
    checklist: ["Did you click any link?", "Did you share any code?", "Have you changed passwords?", "Have you reported it?"],
    default_message: "I may have been targeted by a scam. Reviewing my accounts.",
  },
  {
    id: "unsafe",
    label: "I'M NOT SAFE",
    icon: "Radio",
    tone: "red",
    subtitle: "You feel threatened",
    right_now: [
      "Move toward a public, well-lit place immediately.",
      "Share your live location with someone you trust.",
      "Avoid confrontation.",
      "Keep emergency services one tap away.",
    ],
    next: ["Get into a shop, cafe or public transport", "Note descriptions and any vehicle numbers", "Call for help if the risk grows"],
    checklist: ["Is there a public place nearby?", "Have you shared your location?", "Is your phone charged?", "Do you have an exit route?"],
    default_message: "I don't feel safe right now. I need someone with me.",
  },
];

export const getSituation = (id) => SITUATIONS.find((s) => s.id === id);

export const SCAM_TYPES = [
  "Someone asked for an OTP",
  "Someone demanded money",
  "Suspicious payment link",
  "Fake bank message",
  "Fake job offer",
  "Someone is impersonating someone I know",
];

export const HELP_SCENARIOS = [
  {
    id: "injury",
    label: "Injury",
    steps: [
      "Do not move them unless there is direct danger.",
      "Call emergency services.",
      "Apply gentle pressure to visible bleeding with clean cloth.",
      "Keep them warm and talking.",
    ],
  },
  {
    id: "unconscious",
    label: "Unconscious person",
    steps: [
      "Check for breathing.",
      "Call emergency services immediately.",
      "Place them in the recovery position if trained.",
      "Do not give food or water.",
    ],
  },
  {
    id: "fire",
    label: "Fire",
    steps: [
      "Get everyone out — do not go back in.",
      "Stay low under smoke.",
      "Call the fire service.",
      "Meet at a safe assembly point.",
    ],
  },
  {
    id: "road-accident",
    label: "Road accident",
    steps: [
      "Stop safely, away from traffic.",
      "Turn on hazard lights.",
      "Call emergency services with the exact location.",
      "Warn approaching traffic only if it is safe.",
      "Do not move injured people unless there is direct danger.",
    ],
  },
  {
    id: "unsafe-env",
    label: "Unsafe environment",
    steps: [
      "Leave the area quickly and calmly.",
      "Contact a trusted person.",
      "Call emergency services if needed.",
      "Return only with support or authorities.",
    ],
  },
];

export const DEMO_PRESETS = [
  { id: "collapsed", text: "Someone has collapsed in front of me and isn't responding.", hint: "medical" },
  { id: "lost", text: "I got off at the wrong stop and I don't know where I am.", hint: "lost" },
  { id: "otp", text: "A stranger just asked me for an OTP claiming to be from my bank.", hint: "scam" },
  { id: "child", text: "I lost sight of my child at the mall a minute ago.", hint: "missing_person" },
  { id: "help", text: "Someone in front of me just fell off their bike and is bleeding.", hint: "medical" },
  { id: "followed", text: "I think someone has been following me on my way home.", hint: "unsafe" },
];
